import { Trans } from "@lingui/react/macro";
import { useForm } from "react-hook-form";
import { FilledButton } from "@medoma/components/Button/Button";
import styles from "./ChangeGroup.module.scss";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import {
  changeGroup,
  useEligibleGroups,
  usePatientsActivityOccurrencesAndGroups,
} from "@/api/Activities";
import {
  getUnfulfilledRequirements,
  type IActivityRequirement,
} from "@medoma/models/activityRequirements";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import {
  activityOccurrenceStatusSchema,
  type IGroupOfActivityOccurrence,
  type IUnfinishedActivityOccurrenceStatus,
} from "@medoma/models/activities";
import {
  isGroup,
  timeOfDayDictionary,
  timeOfDaySchema,
} from "@medoma/models/activities";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import { RequirementChip } from "@/components/Chips/RequirementChip";
import { Text } from "@medoma/components/Text/Text";
import { AssignedShiftChip } from "@/components/Chips/AssignedShiftChip";
import type { IScheduledShift } from "@medoma/models/shifts";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import NoResults from "@/components/NoResults/NoResults";
import { useLingui } from "@lingui/react/macro";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import { Heading } from "@medoma/components/Heading/Heading";

const CompetencesAndAssignees = ({
  unfulfilledRequirements,
  assignees,
}: {
  unfulfilledRequirements: IActivityRequirement[];
  assignees: IScheduledShift[];
}) => {
  return (
    <div className={styles.competencesAndAssignees}>
      {unfulfilledRequirements.length > 0 ? (
        <ul className={styles.competences}>
          {unfulfilledRequirements.map((unfulfilledRequirement) => (
            <li key={unfulfilledRequirement}>
              <RequirementChip
                requirement={unfulfilledRequirement}
                state="unfulfilled"
                size="small"
              />
            </li>
          ))}
        </ul>
      ) : undefined}
      {assignees.length > 0 ? (
        <ul className={styles.assignees}>
          {assignees.map((shift) => (
            <li key={shift.id}>
              <AssignedShiftChip
                medicalCompetence={shift.competence}
                shift={shift}
                size="small"
              />
            </li>
          ))}
        </ul>
      ) : undefined}
    </div>
  );
};

const NEW_GROUP_ID = "new-group";

const CustomRadioButton = ({
  group,
}: {
  group: IGroupOfActivityOccurrence;
}) => {
  const { t } = useLingui();

  return (
    <>
      <div className={styles.groupDetails}>
        <Text element="span">
          {group.timeOfDay === timeOfDaySchema.enum.Any
            ? t(timeOfDayDictionary.Any.short)
            : formattedTimeSpan(group.start, group.end)}
        </Text>
        <CompetencesAndAssignees
          assignees={group.assignees}
          unfulfilledRequirements={getUnfulfilledRequirements(group)}
        />
      </div>
      <Text element="span" size="small">
        {group.occurrences.map(({ title }) => title).join(", ")}
      </Text>
    </>
  );
};

interface IFormData {
  groupId: string;
}

export const ChangeGroup = ({
  activityOccurrence,
  onSubmitSuccess,
}: {
  activityOccurrence: Pick<
    IGroupOfActivityOccurrence["occurrences"][number],
    "activityId" | "id" | "start" | "end"
  > & { status: IUnfinishedActivityOccurrenceStatus } & Pick<
      IGroupOfActivityOccurrence["occurrences"][number],
      "patient"
    >;
  onSubmitSuccess?: () => void;
}) => {
  const { t } = useLingui();

  const { activityId, id: occurrenceId, status } = activityOccurrence;
  const patientId = activityOccurrence.patient.id;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<IFormData>();

  const {
    data: eligibleGroups,
    isPending: isPendingGroups,
    isError: isErrorGroups,
  } = useEligibleGroups({ activityId, occurrenceId });

  const {
    data: activityOccurrencesAndGroups,
    isPending: isPendingActivityOccurrencesAndGroups,
  } = usePatientsActivityOccurrencesAndGroups(
    patientId,
    activityOccurrence.start,
    activityOccurrence.start,
  );

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (groupId: string) =>
      changeGroup(activityId, occurrenceId, groupId),
    onError: (error) => {
      setError("groupId", {
        message: deducedError(error),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSubmitSuccess?.();
    },
  });

  const { confirm } = useDialog();

  const activityOccurrenceIsMoveable = async () => {
    if (status !== activityOccurrenceStatusSchema.enum.ongoing) {
      return true;
    }
    const moveActivityOccurrenceConfirmed = await confirm({
      title: t`Flytta aktivitet`,
      message: t`Är du säker på att du vill flytta en aktivitet från ett pågående besök?`,
    });
    return moveActivityOccurrenceConfirmed;
  };

  const isActivityOccurrenceAloneInGroup: "alone" | "notAlone" | "unknown" =
    (() => {
      if (!activityOccurrencesAndGroups) {
        return "unknown";
      }

      const groupStatus = activityOccurrencesAndGroups.some(
        (activityOccurrenceOrGroup) => {
          return (
            isGroup(activityOccurrenceOrGroup) &&
            activityOccurrenceOrGroup.occurrences.some(
              (occurrence) =>
                occurrence.id === occurrenceId &&
                occurrence.activityId === activityId,
            ) &&
            activityOccurrenceOrGroup.occurrences.length === 1
          );
        },
      );

      return groupStatus ? "alone" : "notAlone";
    })();

  const validateAndSubmit = handleSubmit(async (formData) => {
    const { groupId } = formData;
    if (await activityOccurrenceIsMoveable()) {
      mutate(groupId);
    }
  });

  const canMoveToNewGroup = isActivityOccurrenceAloneInGroup === "notAlone";
  const canMoveToExistingGroup =
    eligibleGroups !== undefined && eligibleGroups.length >= 1;
  const canMove = canMoveToNewGroup || canMoveToExistingGroup;
  const groupIdOptions = {
    required: {
      value: true,
      message: t`Välj ett besök`,
    },
  };

  if (isPendingActivityOccurrencesAndGroups || isPendingGroups) {
    return <Loading message={t`Laddar passande besök`} />;
  }

  return (
    <article className={styles.changeGroup}>
      {!canMove ? (
        <NoResults
          message={t`Det finns inget annat besök att flytta denna aktivitet till`}
        />
      ) : (
        <>
          <Heading level="h1" size="h3">
            <Trans>Flytta aktivitet</Trans>
          </Heading>
          <form
            onSubmit={validateAndSubmit}
            className={styles.optionsAndSubmit}
          >
            <RadioButtons
              errorMessage={errors.groupId?.message}
              orientation="vertical"
              width="fill-container"
            >
              {canMoveToNewGroup ? (
                <RadioButton
                  label={{
                    text: t`Nytt besök`,
                  }}
                  visualStyle="framed"
                  value={NEW_GROUP_ID}
                  {...register(`groupId`, groupIdOptions)}
                />
              ) : null}
              <>
                {isErrorGroups ? (
                  <ErrorMessage message={t`Kunde inte hämta besök`} />
                ) : (
                  eligibleGroups.map((group) => (
                    <RadioButton
                      key={group.id}
                      label={{
                        text: `${
                          group.timeOfDay === timeOfDaySchema.enum.Any
                            ? t(timeOfDayDictionary.Any.short)
                            : formattedTimeSpan(group.start, group.end)
                        } ${group.occurrences.map(({ title }) => title).join(", ")}`,
                        component: <CustomRadioButton group={group} />,
                      }}
                      visualStyle="framed"
                      value={group.id}
                      {...register(`groupId`, groupIdOptions)}
                    />
                  ))
                )}
              </>
            </RadioButtons>
            <FilledButton type="submit" disabled={isPending}>
              <Trans>Flytta</Trans>
            </FilledButton>
          </form>
        </>
      )}
    </article>
  );
};
